type UnknownRecord = Record<string, unknown>;

export type Touch = {
  source?: unknown;
  medium?: unknown;
  campaign?: unknown;
  term?: unknown;
  content?: unknown;
  date?: unknown;
};

export type Attribution = {
  firstTouch?: Touch;
  latestTouch?: Touch;
  location?: {
    city?: unknown;
    state?: unknown;
    country?: unknown;
  };
};

const asRecord = (value: unknown): UnknownRecord | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;

const firstValue = (record: UnknownRecord | undefined, keys: string[]) => {
  if (!record) return undefined;
  return keys.map((key) => record[key]).find((value) => value !== undefined && value !== null && value !== "");
};

function toTouch(value: unknown): Touch | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const touch: Touch = {
    source: firstValue(record, ["source", "utm_source", "utmSource"]),
    medium: firstValue(record, ["medium", "utm_medium", "utmMedium"]),
    campaign: firstValue(record, ["campaign", "utm_campaign", "utmCampaign"]),
    term: firstValue(record, ["term", "utm_term", "utmTerm"]),
    content: firstValue(record, ["content", "utm_content", "utmContent"]),
    date: firstValue(record, ["date", "createdAt", "created_at", "timestamp", "occurredAt"]),
  };
  return Object.values(touch).some((item) => item !== undefined) ? touch : undefined;
}

function toPrefixedTouch(record: UnknownRecord, prefix: "first" | "latest"): Touch | undefined {
  const touch: Touch = {
    source: firstValue(record, [`${prefix}_source`, `${prefix}Source`, `${prefix}_utm_source`, `${prefix}UtmSource`]),
    medium: firstValue(record, [`${prefix}_medium`, `${prefix}Medium`, `${prefix}_utm_medium`, `${prefix}UtmMedium`]),
    campaign: firstValue(record, [`${prefix}_campaign`, `${prefix}Campaign`, `${prefix}_utm_campaign`, `${prefix}UtmCampaign`]),
    term: firstValue(record, [`${prefix}_term`, `${prefix}Term`, `${prefix}_utm_term`, `${prefix}UtmTerm`]),
    content: firstValue(record, [`${prefix}_content`, `${prefix}Content`, `${prefix}_utm_content`, `${prefix}UtmContent`]),
    date: firstValue(record, [`${prefix}_touch_at`, `${prefix}TouchAt`, `${prefix}_at`, `${prefix}At`]),
  };
  return Object.values(touch).some((item) => item !== undefined) ? touch : undefined;
}

function toLocation(value: unknown) {
  const record = asRecord(value);
  if (!record) return undefined;
  const location = {
    city: firstValue(record, ["city"]),
    state: firstValue(record, ["state", "region", "state_region"]),
    country: firstValue(record, ["country"]),
  };
  return Object.values(location).some((item) => item !== undefined) ? location : undefined;
}

/**
 * Normalizes the attribution fields returned with existing admin records.
 * This deliberately does not read IP addresses, coordinates, or other precise
 * location fields.
 */
export function getAttribution(value: unknown): Attribution | undefined {
  const record = asRecord(value);
  if (!record) return undefined;

  const attribution = asRecord(
    firstValue(record, [
      "attribution",
      "visitorAttribution",
      "visitor_attribution",
      "marketingAttribution",
      "marketing_attribution",
    ])
  ) || record;

  const firstTouch = toTouch(
    firstValue(attribution, ["firstTouch", "first_touch", "first", "first_utm"])
  ) || toPrefixedTouch(attribution, "first");
  const latestTouch = toTouch(
    firstValue(attribution, ["latestTouch", "latest_touch", "latest", "lastTouch", "last_touch", "latest_utm"])
  ) || toPrefixedTouch(attribution, "latest");
  const location = toLocation(
    firstValue(attribution, ["location", "geo", "geography", "approximateLocation", "approximate_location"])
  ) || toLocation(attribution);

  const result = { firstTouch, latestTouch, location };
  return firstTouch || latestTouch || location ? result : undefined;
}

export function formatAttributionValue(value: unknown): string {
  return value === undefined || value === null || value === "" ? "—" : String(value);
}

export function formatLocation(location: Attribution["location"]): string {
  if (!location) return "—";
  const parts = [location.city, location.state, location.country]
    .filter((item) => item !== undefined && item !== null && item !== "")
    .map(String);
  return parts.length ? parts.join(", ") : "—";
}
